// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The orientation input. Everything device-specific lives here: capability
 * detection, the iOS-only permission dance (which must happen inside a tap),
 * and turning `deviceorientation` / `devicemotion` events into a canonical
 * gravity direction. The angle maths lives in geometry.ts — this module only
 * gathers the vector and tears the listeners down so the sensor genuinely stops.
 */

import type { Vec3 } from './geometry';
import { betaGammaToGravity, accelToGravity } from './geometry';

export type PermissionResult = 'granted' | 'denied' | 'unsupported' | 'error';

export interface OrientationLike {
  beta: number | null;
  gamma: number | null;
}

export interface MotionLike {
  accelerationIncludingGravity?: { x: number | null; y: number | null; z: number | null } | null;
}

export interface GravitySample {
  gravity: Vec3;
  source: 'orientation' | 'motion';
}

/** True if the browser exposes the DeviceOrientation event at all. */
export function isOrientationSupported(): boolean {
  return typeof window !== 'undefined' && 'DeviceOrientationEvent' in window;
}

/** True on the platforms (iOS 13+) that gate the sensors behind an explicit prompt. */
export function needsPermission(): boolean {
  return (
    typeof window !== 'undefined' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (typeof (window as any).DeviceOrientationEvent?.requestPermission === 'function' ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      typeof (window as any).DeviceMotionEvent?.requestPermission === 'function')
  );
}

/** The current UI rotation in degrees (0/90/180/270), or 0 if unknown. */
export function getScreenAngle(): number {
  if (typeof window === 'undefined') return 0;
  const a = window.screen?.orientation?.angle;
  if (typeof a === 'number') return a;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const legacy = (window as any).orientation;
  return typeof legacy === 'number' ? ((legacy % 360) + 360) % 360 : 0;
}

/**
 * Ask for orientation (and motion) access. MUST be called synchronously inside a
 * user gesture on iOS or it throws / is ignored. Elsewhere there is nothing to
 * ask, so we report `granted` and let the caller start listening (a data-timeout
 * then catches the "sensor silently produces nothing" desktop case).
 */
export async function requestOrientationPermission(): Promise<PermissionResult> {
  if (!isOrientationSupported()) return 'unsupported';
  if (!needsPermission()) return 'granted';
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const OE = (window as any).DeviceOrientationEvent;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ME = (window as any).DeviceMotionEvent;
    let orient: string | undefined;
    if (typeof OE?.requestPermission === 'function') orient = await OE.requestPermission();
    // Motion is a best-effort fallback source; a denial there is not fatal.
    if (typeof ME?.requestPermission === 'function') {
      try {
        await ME.requestPermission();
      } catch {
        /* ignore — orientation is enough */
      }
    }
    if (orient === undefined) return 'granted';
    return orient === 'granted' ? 'granted' : 'denied';
  } catch {
    return 'error';
  }
}

/** Pull a gravity direction from a DeviceOrientation event, or null if unusable. */
export function gravityFromOrientation(e: OrientationLike): Vec3 | null {
  if (e.beta == null && e.gamma == null) return null;
  return betaGammaToGravity(e.beta ?? 0, e.gamma ?? 0);
}

/** Pull a gravity direction from a DeviceMotion event, or null if unusable. */
export function gravityFromMotion(e: MotionLike): Vec3 | null {
  const g = e.accelerationIncludingGravity;
  if (!g || (g.x == null && g.y == null && g.z == null)) return null;
  return accelToGravity(g.x ?? 0, g.y ?? 0, g.z ?? 0);
}

export interface CaptureCallbacks {
  onSample: (s: GravitySample) => void;
  /** Fired once if no usable sample arrives within the grace period. */
  onNoData?: () => void;
}

/**
 * Live orientation capture. Instances are single-shot: `start()` then `stop()`.
 * It also stops itself when the page is hidden or unloaded, so a backgrounded
 * tab is never left listening. DeviceOrientation is preferred; DeviceMotion's
 * gravity vector is a fallback for devices that only report the latter.
 */
export class OrientationCapture {
  private listening = false;
  private gotData = false;
  private gotOrientation = false;
  private noDataTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onOrient: (e: Event) => void;
  private readonly onMotion: (e: Event) => void;
  private readonly onHide: () => void;

  constructor(private readonly cb: CaptureCallbacks) {
    this.onOrient = (e: Event) => this.handleOrientation(e as unknown as OrientationLike);
    this.onMotion = (e: Event) => this.handleMotion(e as unknown as MotionLike);
    this.onHide = () => {
      if (document.visibilityState === 'hidden') this.stop();
    };
  }

  get active(): boolean {
    return this.listening;
  }

  start(): void {
    if (this.listening) return;
    this.listening = true;
    this.gotData = false;
    this.gotOrientation = false;
    window.addEventListener('deviceorientation', this.onOrient, { passive: true });
    window.addEventListener('devicemotion', this.onMotion, { passive: true });
    window.addEventListener('pagehide', this.onHide);
    document.addEventListener('visibilitychange', this.onHide);
    this.noDataTimer = setTimeout(() => {
      if (!this.gotData) this.cb.onNoData?.();
    }, 1500);
  }

  stop(): void {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener('deviceorientation', this.onOrient);
    window.removeEventListener('devicemotion', this.onMotion);
    window.removeEventListener('pagehide', this.onHide);
    document.removeEventListener('visibilitychange', this.onHide);
    if (this.noDataTimer !== null) {
      clearTimeout(this.noDataTimer);
      this.noDataTimer = null;
    }
  }

  /** Test hook: feed a synthetic gravity sample as if it came from a sensor. */
  feed(s: GravitySample): void {
    if (!this.listening) return;
    if (s.source === 'orientation') this.gotOrientation = true;
    this.gotData = true;
    this.cb.onSample(s);
  }

  private handleOrientation(e: OrientationLike): void {
    if (!this.listening) return;
    const g = gravityFromOrientation(e);
    if (!g) return;
    this.gotOrientation = true;
    this.gotData = true;
    this.cb.onSample({ gravity: g, source: 'orientation' });
  }

  private handleMotion(e: MotionLike): void {
    if (!this.listening) return;
    // Orientation wins when it is available; motion is only the fallback source.
    if (this.gotOrientation) return;
    const g = gravityFromMotion(e);
    if (!g) return;
    this.gotData = true;
    this.cb.onSample({ gravity: g, source: 'motion' });
  }
}
